import { Plus, Trash2 } from 'lucide-react';
import { Card } from '@dreamtree-org/twreact-ui';

/**
 * The workflow's input contract — the same flat {name,type,description,optional,options}
 * spec dynamic tools use, so buildZodSchemaFromSpec can turn it into the agent-facing
 * schema verbatim (options → z.enum, and the run engine rejects values outside the list).
 * Same row idiom as DynamicToolDetail's ParamsSection.
 *
 * It is also what the Test dialog builds its form from, so filling this in is what makes a
 * workflow testable from the UI rather than only callable by an agent.
 */

const EMPTY_FIELD = { name: '', type: 'string', description: '', optional: false };
const inputClass = 'w-full border rounded-md px-2 py-1 text-sm bg-[var(--app-surface)]';

/** Comma-separated editor for a field's allowed values (string enums). Trailing
 *  separators are tolerated so mid-typing states stay valid. */
export function parseOptions(text) {
  return String(text || '').split(',').map((o) => o.trim()).filter(Boolean);
}

function FieldRow({ f, onUpdate, onRemove }) {
  return (
    <div className="flex items-center gap-2">
      <input className={`${inputClass} flex-1`} placeholder="name" value={f.name} onChange={(e) => onUpdate({ ...f, name: e.target.value })} />
      <select className={inputClass} value={f.type} onChange={(e) => onUpdate({ ...f, type: e.target.value })}>
        <option value="string">string</option>
        <option value="number">number</option>
        <option value="boolean">boolean</option>
      </select>
      <input className={`${inputClass} flex-1`} placeholder="description" value={f.description} onChange={(e) => onUpdate({ ...f, description: e.target.value })} />
      {f.type === 'string' && (
        <input
          className={`${inputClass} flex-1`}
          placeholder="options (comma-sep, optional — restricts to a list)"
          value={(f.options || []).join(', ')}
          onChange={(e) => onUpdate({ ...f, options: parseOptions(e.target.value) })}
        />
      )}
      <label className="flex items-center gap-1 text-xs text-secondary-500 whitespace-nowrap">
        <input type="checkbox" checked={!!f.optional} onChange={(e) => onUpdate({ ...f, optional: e.target.checked })} /> optional
      </label>
      <button type="button" onClick={onRemove} className="p-1 text-red-500 hover:bg-red-500/10 rounded">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export default function InputSchemaSection({ fields, setFields }) {
  const update = (i, next) => setFields(fields.map((f, idx) => (idx === i ? next : f)));
  return (
    <details>
      <summary className="cursor-pointer font-semibold py-2 text-sm">Input fields ({fields.length})</summary>
      <Card className="p-3 space-y-2">
        <p className="text-xs text-secondary-500">
          What an agent must pass when it calls this workflow. Without a schema the model calls it with no arguments.
          A string field with options becomes a fixed choice list — anything else is rejected at run time.
        </p>
        {fields.map((f, i) => (
          <FieldRow
            key={i}
            f={f}
            onUpdate={(next) => update(i, next)}
            onRemove={() => setFields(fields.filter((_, idx) => idx !== i))}
          />
        ))}
        <button type="button" onClick={() => setFields([...fields, { ...EMPTY_FIELD }])} className="flex items-center gap-1 text-xs text-primary-600">
          <Plus size={14} /> Add field
        </button>
      </Card>
    </details>
  );
}
